import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useAttendanceSettings, useAttendanceSettingsActions } from '@/hooks/useAttendanceSettings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Settings,
  Calendar,
  Gift,
  FileText,
  Save,
  Bell,
  Plus,
  Trash2,
  Edit2,
  X,
  Briefcase,
  Palette,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatWorkingDays } from '@/components/attendance/WorkingDaysEditor';

/**
 * Stat pill component for displaying key metrics
 */
const StatPill = ({ icon: Icon, label, value, color = "slate" }) => {
  const colorConfig = {
    slate: { bg: "bg-slate-50", icon: "bg-slate-100 text-slate-600", value: "text-slate-800" },
    indigo: { bg: "bg-indigo-50", icon: "bg-indigo-100 text-indigo-600", value: "text-indigo-700" },
    emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600", value: "text-emerald-700" },
    amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-600", value: "text-amber-700" }
  };

  const colors = colorConfig[color] || colorConfig.slate;

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-full ${colors.bg}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colors.icon}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-slate-500 font-medium">{label}</span>
        <span className={`text-sm font-semibold ${colors.value}`}>{value}</span>
      </div>
    </div>
  );
};

/**
 * Working day button component
 */
const DayButton = ({ day, value, isSelected, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    className={`w-11 h-11 rounded-full font-medium text-sm transition-all duration-200 ${
      isSelected
        ? 'bg-slate-800 text-white shadow-sm'
        : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
    }`}
  >
    {day}
  </button>
);

/**
 * Setting toggle row component
 */
const SettingToggle = ({ id, label, description, checked, onChange, color = "slate" }) => {
  const colorConfig = {
    slate: "bg-slate-50 border-slate-200",
    indigo: "bg-indigo-50/50 border-indigo-200",
    amber: "bg-amber-50/50 border-amber-200"
  };

  return (
    <div className={`flex items-center justify-between gap-4 p-4 rounded-xl border ${colorConfig[color]}`}>
      <div className="space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium text-slate-800">
          {label}
        </Label>
        <p className="text-xs text-slate-500">
          {description}
        </p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-slate-800"
      />
    </div>
  );
};

/**
 * Entitlement type row component
 */
const EntitlementTypeRow = ({ entitlement, isEditing, onEdit, onDelete, onUpdate, onCancelEdit, processing }) => {
  const [localData, setLocalData] = useState({
    name: entitlement.name,
    description: entitlement.description || '',
    default_days: entitlement.default_days
  });

  useEffect(() => {
    setLocalData({
      name: entitlement.name,
      description: entitlement.description || '',
      default_days: entitlement.default_days
    });
  }, [entitlement, isEditing]);

  const handleSave = () => {
    if (localData.name.trim()) {
      onUpdate(entitlement.id, {
        name: localData.name.trim(),
        description: localData.description.trim(),
        default_days: localData.default_days
      });
    }
  };

  return (
    <div className="relative pl-4 py-4 pr-4 bg-white rounded-xl border border-slate-200">
      {/* Accent bar */}
      <div className="absolute left-0 top-3 bottom-3 w-1 rounded-full bg-indigo-500" />

      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Name</Label>
              <Input
                value={localData.name}
                onChange={(e) => setLocalData(prev => ({ ...prev, name: e.target.value }))}
                className="rounded-full h-9"
                placeholder="Entitlement name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Description</Label>
              <Input
                value={localData.description}
                onChange={(e) => setLocalData(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-full h-9"
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Default Days</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={localData.default_days}
                onChange={(e) => setLocalData(prev => ({ ...prev, default_days: parseFloat(e.target.value) || 0 }))}
                className="rounded-full h-9"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!localData.name.trim() || processing}
              className="rounded-full h-8 px-4"
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancelEdit}
              className="rounded-full h-8 px-4"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800">{entitlement.name}</span>
              <Badge variant="secondary" className="rounded-full text-xs bg-indigo-50 text-indigo-700 border-0">
                {entitlement.default_days} days
              </Badge>
            </div>
            {entitlement.description && (
              <p className="text-sm text-slate-500 mt-0.5">{entitlement.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(entitlement.id)}
              disabled={processing}
              className="h-8 w-8 p-0 rounded-full hover:bg-slate-100"
            >
              <Edit2 className="h-4 w-4 text-slate-500" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(entitlement.id)}
              disabled={processing}
              className="h-8 w-8 p-0 rounded-full hover:bg-rose-50"
            >
              <Trash2 className="h-4 w-4 text-rose-500" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Leave type row component
 */
const LeaveTypeRow = ({ leaveType, isEditing, onEdit, onDelete, onUpdate, onCancelEdit, processing }) => {
  const isSystemDefault = leaveType.is_system_default;

  const [localData, setLocalData] = useState({
    name: leaveType.name,
    description: leaveType.description || '',
    color: leaveType.color,
    deducts_from_balance: leaveType.deducts_from_balance,
    required_permission: leaveType.required_permission
  });

  useEffect(() => {
    setLocalData({
      name: leaveType.name,
      description: leaveType.description || '',
      color: leaveType.color,
      deducts_from_balance: leaveType.deducts_from_balance,
      required_permission: leaveType.required_permission
    });
  }, [leaveType, isEditing]);

  const handleSave = () => {
    if (localData.name.trim()) {
      onUpdate(leaveType.id, localData);
    }
  };

  return (
    <div className="relative pl-4 py-4 pr-4 bg-white rounded-xl border border-slate-200">
      {/* Accent bar with leave type color */}
      <div
        className="absolute left-0 top-3 bottom-3 w-1 rounded-full"
        style={{ backgroundColor: leaveType.color }}
      />

      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Name</Label>
              <Input
                value={localData.name}
                onChange={(e) => setLocalData(prev => ({ ...prev, name: e.target.value }))}
                className="rounded-full h-9"
                placeholder="Leave type name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Description</Label>
              <Input
                value={localData.description}
                onChange={(e) => setLocalData(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-full h-9"
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={localData.color}
                  onChange={(e) => setLocalData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-9 h-9 rounded-full border border-slate-200 cursor-pointer overflow-hidden"
                  style={{ padding: '2px' }}
                />
                <span className="text-sm text-slate-500">{localData.color}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SettingToggle
              id={`deducts-balance-${leaveType.id}`}
              label="Deducts from Balance"
              description="Reduces annual leave balance"
              checked={localData.deducts_from_balance}
              onChange={(checked) => setLocalData(prev => ({ ...prev, deducts_from_balance: checked }))}
              color="indigo"
            />
            <SettingToggle
              id={`restrict-admin-${leaveType.id}`}
              label="Restrict to Admins"
              description="Only managers can request"
              checked={localData.required_permission === 'attendance.manage'}
              onChange={(checked) => setLocalData(prev => ({
                ...prev,
                required_permission: checked ? 'attendance.manage' : null
              }))}
              color="amber"
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!localData.name.trim() || processing}
              className="rounded-full h-8 px-4"
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancelEdit}
              className="rounded-full h-8 px-4"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: leaveType.color }}
              />
              <span className="font-medium text-slate-800">{leaveType.name}</span>
              {isSystemDefault && (
                <Badge variant="secondary" className="rounded-full text-xs bg-slate-100 text-slate-600 border-0">
                  System
                </Badge>
              )}
              {leaveType.required_permission === 'attendance.manage' && (
                <Badge variant="secondary" className="rounded-full text-xs bg-amber-50 text-amber-700 border-0">
                  Admins Only
                </Badge>
              )}
              {leaveType.deducts_from_balance && (
                <Badge variant="secondary" className="rounded-full text-xs bg-indigo-50 text-indigo-700 border-0">
                  Deducts Balance
                </Badge>
              )}
            </div>
            {leaveType.description && (
              <p className="text-sm text-slate-500 mt-0.5">{leaveType.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(leaveType.id)}
              disabled={processing}
              className="h-8 w-8 p-0 rounded-full hover:bg-slate-100"
            >
              <Edit2 className="h-4 w-4 text-slate-500" />
            </Button>
            {!isSystemDefault && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(leaveType.id)}
                disabled={processing}
                className="h-8 w-8 p-0 rounded-full hover:bg-rose-50"
              >
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Info banner component
 */
const InfoBanner = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-slate-600" />
    </div>
    <div>
      <p className="font-medium text-slate-800 text-sm">{title}</p>
      <p className="text-xs text-slate-500 mt-0.5">{description}</p>
    </div>
  </div>
);

export const SettingsPage = () => {
  const navigate = useNavigate();
  const { company, can, loading: authLoading, permissionsLoaded } = useAuth();

  usePageTitle('Attendance Settings');

  // Auth is ready when not loading AND permissions have been loaded
  const authReady = !authLoading && permissionsLoaded;

  // Redirect non-authorized users (only after auth has loaded)
  useEffect(() => {
    if (authReady && !can('attendance.manage')) {
      toast.error('You do not have permission to access attendance settings');
      navigate('/dashboard');
    }
  }, [authReady, can, navigate]);

  // Data from custom hook
  const {
    leaveSettings,
    customEntitlements,
    leaveTypes,
    loading,
    updateLeaveSettings,
    updateCustomEntitlementsList,
    updateLeaveTypesList,
    defaultNewEntitlement,
    defaultNewLeaveType
  } = useAttendanceSettings(company?.id);

  // Local UI state
  const [newEntitlement, setNewEntitlement] = useState(defaultNewEntitlement);
  const [editingId, setEditingId] = useState(null);
  const [newLeaveType, setNewLeaveType] = useState(defaultNewLeaveType);
  const [editingLeaveTypeId, setEditingLeaveTypeId] = useState(null);
  const [showAddEntitlement, setShowAddEntitlement] = useState(false);
  const [showAddLeaveType, setShowAddLeaveType] = useState(false);

  // Callbacks for actions hook
  const actionCallbacks = useMemo(() => ({
    onEntitlementAdded: (data) => {
      updateCustomEntitlementsList(prev => [...prev, data]);
    },
    onEntitlementUpdated: (id, data) => {
      updateCustomEntitlementsList(prev => prev.map(item => item.id === id ? data : item));
      setEditingId(null);
    },
    onEntitlementDeleted: (id) => {
      updateCustomEntitlementsList(prev => prev.filter(item => item.id !== id));
    },
    onLeaveTypeAdded: (data) => {
      updateLeaveTypesList(prev => [...prev, data]);
    },
    onLeaveTypeUpdated: (id, data) => {
      updateLeaveTypesList(prev => prev.map(item => item.id === id ? data : item));
      setEditingLeaveTypeId(null);
    },
    onLeaveTypeDeleted: (id) => {
      updateLeaveTypesList(prev => prev.filter(item => item.id !== id));
    }
  }), [updateCustomEntitlementsList, updateLeaveTypesList]);

  // Actions from custom hook
  const {
    saveLeaveSettings,
    addCustomEntitlement,
    updateCustomEntitlement,
    deleteCustomEntitlement,
    addLeaveType,
    updateLeaveType,
    deleteLeaveType,
    saving,
    processing
  } = useAttendanceSettingsActions(company?.id, actionCallbacks);

  /**
   * Handle save leave settings
   */
  const handleSave = useCallback(async () => {
    const result = await saveLeaveSettings(leaveSettings);
    if (result.success) {
      toast.success('Settings updated successfully');
    } else {
      toast.error(result.error || 'Failed to save settings');
    }
  }, [saveLeaveSettings, leaveSettings]);

  /**
   * Handle add custom entitlement
   */
  const handleAddCustomEntitlement = useCallback(async () => {
    if (!newEntitlement.name.trim()) {
      toast.error('Please enter a name for the entitlement');
      return;
    }

    const result = await addCustomEntitlement(newEntitlement, customEntitlements.length);
    if (result.success) {
      toast.success('Custom entitlement type added');
      setNewEntitlement(defaultNewEntitlement);
      setShowAddEntitlement(false);
    } else {
      toast.error(result.error || 'Failed to add custom entitlement type');
    }
  }, [addCustomEntitlement, newEntitlement, customEntitlements.length, defaultNewEntitlement]);

  /**
   * Handle update custom entitlement
   */
  const handleUpdateCustomEntitlement = useCallback(async (id, updates) => {
    const result = await updateCustomEntitlement(id, updates);
    if (result.success) {
      toast.success('Custom entitlement type updated');
    } else {
      toast.error(result.error || 'Failed to update custom entitlement type');
    }
  }, [updateCustomEntitlement]);

  /**
   * Handle delete custom entitlement
   */
  const handleDeleteCustomEntitlement = useCallback(async (id) => {
    if (!confirm('Are you sure you want to delete this custom entitlement type? This will remove it from all users.')) {
      return;
    }

    const result = await deleteCustomEntitlement(id);
    if (result.success) {
      toast.success('Custom entitlement type deleted');
    } else {
      toast.error(result.error || 'Failed to delete custom entitlement type');
    }
  }, [deleteCustomEntitlement]);

  /**
   * Handle add leave type
   */
  const handleAddLeaveType = useCallback(async () => {
    if (!newLeaveType.name.trim()) {
      toast.error('Please enter a name for the leave type');
      return;
    }

    const result = await addLeaveType(newLeaveType);
    if (result.success) {
      toast.success('Leave type added');
      setNewLeaveType(defaultNewLeaveType);
      setShowAddLeaveType(false);
    } else {
      toast.error(result.error || 'Failed to add leave type');
    }
  }, [addLeaveType, newLeaveType, defaultNewLeaveType]);

  /**
   * Handle update leave type
   */
  const handleUpdateLeaveType = useCallback(async (id, updates) => {
    const result = await updateLeaveType(id, updates);
    if (result.success) {
      toast.success('Leave type updated');
    } else {
      toast.error(result.error || 'Failed to update leave type');
    }
  }, [updateLeaveType]);

  /**
   * Handle delete leave type
   */
  const handleDeleteLeaveType = useCallback(async (id) => {
    if (!confirm('Are you sure you want to delete this leave type? This will affect any leave requests using this type.')) {
      return;
    }

    const result = await deleteLeaveType(id);
    if (result.success) {
      toast.success('Leave type deleted');
    } else {
      toast.error(result.error || 'Failed to delete leave type');
    }
  }, [deleteLeaveType]);

  /**
   * Handle leave settings field change
   */
  const handleLeaveSettingsChange = useCallback((field, value) => {
    updateLeaveSettings({ [field]: value });
  }, [updateLeaveSettings]);

  /**
   * Handle new entitlement field change
   */
  const handleNewEntitlementChange = useCallback((field, value) => {
    setNewEntitlement(prev => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Handle new leave type field change
   */
  const handleNewLeaveTypeChange = useCallback((field, value) => {
    setNewLeaveType(prev => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Toggle working day in the array
   */
  const toggleWorkingDay = useCallback((dayValue) => {
    const currentDays = leaveSettings.working_days || [1, 2, 3, 4, 5];
    let newDays;

    if (currentDays.includes(dayValue)) {
      // Remove the day
      newDays = currentDays.filter(d => d !== dayValue);
      // Prevent empty array - require at least one working day
      if (newDays.length === 0) {
        toast.error('At least one working day is required');
        return;
      }
    } else {
      // Add the day and sort
      newDays = [...currentDays, dayValue].sort((a, b) => a - b);
    }

    updateLeaveSettings({ working_days: newDays });
  }, [leaveSettings.working_days, updateLeaveSettings]);

  // Show loading while auth is being verified
  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }

  // Don't render anything if user doesn't have permission (redirect will happen via useEffect)
  if (!can('attendance.manage')) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="space-y-6 max-w-4xl"
    >
      {/* Hero Section */}
      <div className="rounded-2xl bg-white border border-slate-200 p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <Settings className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-800 tracking-tight">
                Attendance Settings
              </h1>
              <p className="text-slate-500 mt-1">
                Configure company-wide attendance and leave policies
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full px-6 bg-slate-800 hover:bg-slate-700"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-slate-100">
          <StatPill
            icon={Calendar}
            label="Annual Leave"
            value={`${leaveSettings.default_annual_leave} days`}
            color="indigo"
          />
          <StatPill
            icon={Briefcase}
            label="Working Days"
            value={formatWorkingDays(leaveSettings.working_days)}
            color="slate"
          />
          <StatPill
            icon={Gift}
            label="Custom Types"
            value={customEntitlements.length}
            color="emerald"
          />
          <StatPill
            icon={FileText}
            label="Leave Types"
            value={leaveTypes.length}
            color="amber"
          />
        </div>
      </div>

      {/* Leave Policy Settings */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Leave Policy</h2>
            <p className="text-sm text-slate-500">Default leave entitlements for new employees</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Annual Leave Days */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
            <Label htmlFor="annual-leave" className="text-sm font-medium text-slate-700">
              Annual Leave Days
            </Label>
            <Input
              id="annual-leave"
              type="number"
              min="0"
              value={leaveSettings.default_annual_leave}
              onChange={(e) => handleLeaveSettingsChange('default_annual_leave', parseFloat(e.target.value) || 0)}
              placeholder="25"
              className="mt-2 rounded-full h-10 max-w-[200px]"
            />
            <p className="text-xs text-slate-500 mt-2">Number of paid annual leave days per year</p>
          </div>

          {/* Company Working Days */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="h-4 w-4 text-slate-600" />
              <Label className="text-sm font-medium text-slate-700">Company Working Days</Label>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Select which days your company typically operates. This affects how leave days are calculated.
            </p>
            <div className="flex gap-2 flex-wrap">
              {[
                { day: 'M', value: 1 },
                { day: 'T', value: 2 },
                { day: 'W', value: 3 },
                { day: 'T', value: 4 },
                { day: 'F', value: 5 },
                { day: 'S', value: 6 },
                { day: 'S', value: 7 }
              ].map(({ day, value }) => (
                <DayButton
                  key={value}
                  day={day}
                  value={value}
                  isSelected={leaveSettings.working_days?.includes(value)}
                  onClick={toggleWorkingDay}
                />
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Individual employees can have different patterns set in their attendance profile.
            </p>
          </div>

          {/* Settings Toggles */}
          <SettingToggle
            id="allow-carry-over"
            label="Allow Carry Over"
            description="Allow employees to carry unused leave days to the next year"
            checked={leaveSettings.allow_carry_over}
            onChange={(checked) => handleLeaveSettingsChange('allow_carry_over', checked)}
          />

          {leaveSettings.allow_carry_over && (
            <div className="p-4 rounded-xl bg-indigo-50/50 border border-indigo-200 ml-4">
              <Label htmlFor="carry-over" className="text-sm font-medium text-slate-700">
                Maximum Carry Over Days
              </Label>
              <Input
                id="carry-over"
                type="number"
                min="0"
                value={leaveSettings.default_carry_over_days}
                onChange={(e) => handleLeaveSettingsChange('default_carry_over_days', parseFloat(e.target.value) || 0)}
                placeholder="5"
                className="mt-2 rounded-full h-10 max-w-[200px]"
              />
              <p className="text-xs text-slate-500 mt-2">Maximum days that can be carried over from previous year</p>
            </div>
          )}

          <SettingToggle
            id="bank-holidays"
            label="Bank Holidays"
            description="Employees are entitled to standard bank holidays in addition to annual leave"
            checked={leaveSettings.entitled_to_bank_holidays}
            onChange={(checked) => handleLeaveSettingsChange('entitled_to_bank_holidays', checked)}
          />

          <InfoBanner
            icon={Bell}
            title="Automatic Entitlements"
            description="These settings are automatically applied when new users join your company. Existing users keep their current entitlements unless manually updated."
          />
        </div>
      </div>

      {/* Custom Entitlement Types */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Custom Entitlement Types</h2>
              <p className="text-sm text-slate-500">Create custom leave entitlement types</p>
            </div>
          </div>
          <Button
            onClick={() => setShowAddEntitlement(true)}
            className="rounded-full px-4 bg-slate-800 hover:bg-slate-700"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Type
          </Button>
        </div>

        {customEntitlements.length > 0 ? (
          <div className="space-y-3">
            {customEntitlements.map((entitlement) => (
              <EntitlementTypeRow
                key={entitlement.id}
                entitlement={entitlement}
                isEditing={editingId === entitlement.id}
                onEdit={setEditingId}
                onDelete={handleDeleteCustomEntitlement}
                onUpdate={handleUpdateCustomEntitlement}
                onCancelEdit={() => setEditingId(null)}
                processing={processing}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Gift className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm">No custom entitlement types created yet</p>
            <p className="text-xs text-slate-400 mt-1">Add types like study leave, compassionate leave, etc.</p>
          </div>
        )}

        <div className="mt-4">
          <InfoBanner
            icon={AlertCircle}
            title="Custom Entitlements"
            description="Custom entitlement types will be available when editing individual user entitlements. The default days will be applied automatically to new users."
          />
        </div>
      </div>

      {/* Leave Request Types */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Leave Request Types</h2>
              <p className="text-sm text-slate-500">Customize the types of leave that employees can request</p>
            </div>
          </div>
          <Button
            onClick={() => setShowAddLeaveType(true)}
            className="rounded-full px-4 bg-slate-800 hover:bg-slate-700"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Type
          </Button>
        </div>

        {leaveTypes.length > 0 ? (
          <div className="space-y-3">
            {leaveTypes.map((leaveType) => (
              <LeaveTypeRow
                key={leaveType.id}
                leaveType={leaveType}
                isEditing={editingLeaveTypeId === leaveType.id}
                onEdit={setEditingLeaveTypeId}
                onDelete={handleDeleteLeaveType}
                onUpdate={handleUpdateLeaveType}
                onCancelEdit={() => setEditingLeaveTypeId(null)}
                processing={processing}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm">No leave types configured</p>
          </div>
        )}

        <div className="mt-4">
          <InfoBanner
            icon={Palette}
            title="Leave Types"
            description="System default leave types can be edited to customize their name, description, and color, but cannot be deleted. Custom leave types can be fully managed."
          />
        </div>
      </div>

      {/* Add Entitlement Dialog */}
      <Dialog open={showAddEntitlement} onOpenChange={setShowAddEntitlement}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Entitlement Type</DialogTitle>
            <DialogDescription>
              Create a new custom leave entitlement type for your company
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-name">Name *</Label>
              <Input
                id="new-name"
                placeholder="e.g., Study Leave"
                value={newEntitlement.name}
                onChange={(e) => handleNewEntitlementChange('name', e.target.value)}
                className="rounded-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-description">Description</Label>
              <Input
                id="new-description"
                placeholder="Optional description"
                value={newEntitlement.description}
                onChange={(e) => handleNewEntitlementChange('description', e.target.value)}
                className="rounded-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-days">Default Days</Label>
              <Input
                id="new-days"
                type="number"
                min="0"
                step="0.5"
                value={newEntitlement.default_days}
                onChange={(e) => handleNewEntitlementChange('default_days', parseFloat(e.target.value) || 0)}
                className="rounded-full max-w-[120px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddEntitlement(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCustomEntitlement}
              disabled={!newEntitlement.name.trim() || processing}
              className="rounded-full bg-slate-800 hover:bg-slate-700"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Entitlement Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Leave Type Dialog */}
      <Dialog open={showAddLeaveType} onOpenChange={setShowAddLeaveType}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Leave Request Type</DialogTitle>
            <DialogDescription>
              Create a new type of leave that employees can request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-leave-name">Name *</Label>
              <Input
                id="new-leave-name"
                placeholder="e.g., Training Leave"
                value={newLeaveType.name}
                onChange={(e) => handleNewLeaveTypeChange('name', e.target.value)}
                className="rounded-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-leave-description">Description</Label>
              <Input
                id="new-leave-description"
                placeholder="Optional description"
                value={newLeaveType.description}
                onChange={(e) => handleNewLeaveTypeChange('description', e.target.value)}
                className="rounded-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-leave-color">Color</Label>
              <div className="flex items-center gap-3">
                <input
                  id="new-leave-color"
                  type="color"
                  value={newLeaveType.color}
                  onChange={(e) => handleNewLeaveTypeChange('color', e.target.value)}
                  className="w-10 h-10 rounded-full border border-slate-200 cursor-pointer overflow-hidden"
                  style={{ padding: '2px' }}
                />
                <span className="text-sm text-slate-500">{newLeaveType.color}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddLeaveType(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddLeaveType}
              disabled={!newLeaveType.name.trim() || processing}
              className="rounded-full bg-slate-800 hover:bg-slate-700"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Leave Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
